/**
 * The shop's own number for a garment — "RN-7" beside our code (owner, 2026-10-26, canvas "Garment
 * numbers flow", direction B). Shops that numbered every garment 1, 2, 3… across designs keep
 * reading those numbers off their tags; our code is issued as always.
 *
 * <p>
 * <strong>Mirrors `ReferenceNumbers.cs` on the server</strong>, which stores the same one spelling
 * and refuses the same numbers. Change both or neither.
 * </p>
 */

export const maxReferenceLength = 20;

/** One spelling per number: "007", " 7 " and "RN-7" are all "7". Null for a blank. */
export function normalizeReference(typed: string | null | undefined): string | null {
  let trimmed = (typed ?? '').trim().toUpperCase();

  // "RN-7" is how the number is shown, so it is also how a clerk may type it back.
  if (trimmed.startsWith('RN-') || trimmed.startsWith('RN ')) {
    trimmed = trimmed.slice(3).trim();
  }

  if (trimmed === '') {
    return null;
  }

  if (/^\d+$/.test(trimmed)) {
    return trimmed.replace(/^0+/, '') || '0';
  }

  return trimmed;
}

/** Letters, digits, hyphen and slash, up to twenty — what a tag carries. Takes a normalized number. */
export function isValidReference(normalized: string): boolean {
  return (
    normalized.length > 0 &&
    normalized.length <= maxReferenceLength &&
    /^[A-Z0-9/-]+$/.test(normalized)
  );
}

/** "RN-7". */
export function referenceLabel(referenceNumber: string): string {
  return `RN-${referenceNumber}`;
}

/** "BLZ-38-03 (RN-7)", or the code alone when the garment has no number of the shop's own. */
export function withReference(serialCode: string, referenceNumber?: string | null): string {
  return referenceNumber ? `${serialCode} (${referenceLabel(referenceNumber)})` : serialCode;
}

/** "21", "22", "23"… — "Fill from 21" down a list of garments. */
export function referencesFrom(start: number, count: number): string[] {
  return Array.from({ length: Math.max(0, count) }, (_, index) => String(start + index));
}

/** A typed number already on a live garment — the server's `ReferenceNumberTakenDto`. */
export interface TakenReference {
  referenceNumber: string;
  unitId: number;
  serialCode: string;
  itemName: string;
}

/**
 * Why each typed number cannot be booked in, row by row — null where it can. Blank is fine (the
 * number is optional); a number on another garment, the same number twice in the list, or
 * characters a tag cannot carry are not, which is what holds Book in.
 */
export function referenceProblems(
  typed: readonly string[],
  taken: readonly TakenReference[],
): (string | null)[] {
  const numbers = typed.map(normalizeReference);
  const seen = new Map<string, number>();

  for (const number of numbers) {
    if (number !== null) {
      seen.set(number, (seen.get(number) ?? 0) + 1);
    }
  }

  const onGarment = new Map(taken.map((row) => [row.referenceNumber, row]));

  return numbers.map((number) => {
    if (number === null) {
      return null;
    }

    if (!isValidReference(number)) {
      return 'Letters, digits, - and / only';
    }

    const other = onGarment.get(number);

    if (other) {
      return `Already on ${other.serialCode} · ${other.itemName}`;
    }

    return (seen.get(number) ?? 0) > 1 ? 'Typed twice in this list' : null;
  });
}

/**
 * `PUT /api/item-unit/{id}/reference-number` — one garment's number alone, given, changed or (blank)
 * taken away. The phone's garment page sends this rather than the whole-garment update, which also
 * restates the garment's own rent and would withdraw a price it cannot show.
 */
export function referenceNumberChange(typed: string): { value: string | null } {
  return { value: normalizeReference(typed) };
}

/**
 * Why a number typed for one garment cannot be sent, or null — blank is fine (it takes the number
 * away). Whether another garment carries it is the server's answer, which names that garment.
 */
export function referenceNumberProblem(typed: string): string | null {
  const number = normalizeReference(typed);

  return number !== null && !isValidReference(number)
    ? `Letters, digits, - and / only, up to ${maxReferenceLength}`
    : null;
}

/** The typed numbers worth asking the server about: cleaned, valid, each once. */
export function referencesToCheck(typed: readonly string[]): string[] {
  return [
    ...new Set(
      typed
        .map(normalizeReference)
        .filter((number): number is string => number !== null && isValidReference(number)),
    ),
  ];
}

/** The longest run {@link referencesOf} expands. */
export const maxRun = 500;

/**
 * The numbers in one cell of a sheet or one typed box: separated by commas, semicolons, spaces or
 * lines, and a run of plain numbers written "1-5" or "1–5" read as each of them. A dash between
 * anything else ("A-12") stays part of one number. A run longer than {@link maxRun} is read as
 * written, never expanded — a typo of "1-5000" must not make five thousand garments.
 */
export function referencesOf(cell: string): string[] {
  const parts = cell
    .split(/[,;\s]+/)
    .map((part) => part.trim())
    .filter((part) => part !== '');

  return parts.flatMap((part) => {
    const run = /^(\d+)\s*[-–]\s*(\d+)$/.exec(part);

    if (run) {
      const from = Number(run[1]);
      const to = Number(run[2]);

      if (to >= from && to - from < maxRun) {
        return referencesFrom(from, to - from + 1);
      }
    }

    return [part];
  });
}
